import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchArticles, type SanityArticle } from "@/lib/sanityQueries";
import { urlFor, isPreviewMode } from "@/lib/sanity";
import { getCategoryImage } from "@/lib/categoryImages";
import { registerCategoryColor } from "@/lib/categoryColors";
import type { Article } from "@/components/NewsSection";
import type { ArticleTag } from "@/lib/articleTags";

const validTags: ArticleTag[] = ["breaking", "feature", "opinion", "interview", "scoop"];

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? "" : "s"} ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Count words in a Portable Text body array and estimate read time */
function estimateReadTime(body?: any[]): string {
  if (!body || body.length === 0) return "5 min read";
  let wordCount = 0;
  for (const block of body) {
    if (block._type === "block" && Array.isArray(block.children)) {
      for (const child of block.children) {
        if (typeof child.text === "string") {
          wordCount += child.text.split(/\s+/).filter(Boolean).length;
        }
      }
    }
  }
  const minutes = Math.max(1, Math.round(wordCount / 230));
  return `${minutes} min read`;
}

export function sanityToArticle(sa: SanityArticle, index: number): Article {
  const categoryName = sa.category?.title ?? "General";
  // Register Sanity color override if provided
  if (sa.category?.color) {
    registerCategoryColor(categoryName, sa.category.color);
  }
  const tag = validTags.includes(sa.tag as ArticleTag) ? (sa.tag as ArticleTag) : undefined;

  let image: string;
  try {
    image = sa.mainImage ? urlFor(sa.mainImage).width(960).height(540).auto('format').quality(80).url() : getCategoryImage(categoryName);
  } catch {
    image = getCategoryImage(categoryName);
  }

  let authorData: Article["author"] | undefined;
  if (sa.author) {
    let avatarUrl: string | undefined;
    try {
      avatarUrl = sa.author.avatar ? urlFor(sa.author.avatar).width(80).height(80).url() : undefined;
    } catch {
      avatarUrl = undefined;
    }
    authorData = {
      name: sa.author.name,
      slug: sa.author.slug?.current ?? sa.author._id,
      role: sa.author.role,
      avatar: avatarUrl ?? `https://api.dicebear.com/9.x/notionists/svg?seed=${sa.author.name}`,
    };
  }

  const isScheduled = isPreviewMode() && sa.publishedAt && new Date(sa.publishedAt) > new Date();

  return {
    id: 1000 + index,
    slug: sa.slug.current,
    title: isScheduled ? `[SCHEDULED] ${sa.title}` : sa.title,
    excerpt: sa.excerpt ?? "",
    category: categoryName,
    date: isScheduled
      ? `Scheduled: ${formatDate(sa.publishedAt)}`
      : formatDate(sa.publishedAt),
    readTime: estimateReadTime(sa.body),
    image,
    lqip: sa.lqip ?? undefined,
    featured: index === 0,
    tag,
    author: authorData,
    body: sa.body,
    sourcingDisclosure: sa.sourcing_disclosure || undefined,
    corrections: sa.corrections || undefined,
  };
}

export function useSanityArticles() {
  const select = useCallback(
    (data: SanityArticle[]) => data.map(sanityToArticle),
    []
  );

  return useQuery({
    queryKey: ["sanity-articles"],
    queryFn: fetchArticles,
    staleTime: 5 * 60 * 1000,
    structuralSharing: true,
    select,
  });
}
